/**
 * API client for Semantle game
 * @param {string} apiUrl - Base URL of the API server
 */
export function createApiClient(apiUrl = '') {
  // If apiUrl is provided, use it. Otherwise:
  // - In browser: use relative URLs (same origin) or current origin
  // - In Node.js: use localhost:3000
  let baseUrl = apiUrl;
  if (!baseUrl) {
    if (typeof window !== 'undefined') {
      // In browser: use same origin (relative URLs) for production,
      // or use current origin for development
      baseUrl = '';
    } else {
      // In Node.js (SSR or tests)
      baseUrl = 'http://localhost:3000';
    }
  }

  /**
   * Submit a word guess
   * @param {string} guess - The word to guess
   * @param {string} lang - Language code (e.g., 'en', 'fr')
   * @param {string} dayKey - Optional day key to play a specific day's word
   * @returns {Promise<Object>} Response with score, inVocab, congratulations, etc.
   */
  async function submitGuess(guess, lang = 'en', dayKey = null) {
    const url = `${baseUrl}/api/guess`;
    const body = { guess: guess.trim(), lang };
    if (dayKey) {
      body.dayKey = dayKey;
    }
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 15000) : null;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      });
      if (timeoutId) clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Failed to submit guess');
      }

      return data;
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error('Request timed out. Check your connection.');
      }
      throw err;
    }
  }

  /**
   * Get metadata about the current puzzle
   * @param {string} lang - Language code (e.g., 'en', 'el', 'fr')
   * @param {string} dayKey - Optional day key (user's local date). When provided, server loads this day's data.
   * @returns {Promise<Object>} Metadata with dayKey and vocabSize
   */
  async function getMeta(lang = 'en', dayKey = null) {
    let url = `${baseUrl}/api/meta?lang=${encodeURIComponent(lang)}`;
    if (dayKey) {
      url += `&dayKey=${encodeURIComponent(dayKey)}`;
    }
    const response = await fetch(url);
    return response.json();
  }

  /**
   * Find closest words from vocabulary
   * @param {string} word - The word to find closest matches for
   * @param {string} lang - Language code (e.g., 'en', 'fr')
   * @param {number} maxResults - Maximum number of suggestions (default: 5)
   * @param {string} dayKey - Optional day key to use a specific day's vocabulary
   * @returns {Promise<Object>} Response with exactMatch, suggestions, and distances
   */
  async function findClosestWords(word, lang = 'en', maxResults = 5, dayKey = null) {
    const url = `${baseUrl}/api/find-closest`;
    try {
      const body = { word: word.trim(), lang, maxResults };
      if (dayKey) {
        body.dayKey = dayKey;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Accept': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      // Get response as text first to ensure UTF-8 encoding is preserved
      const text = await response.text();
      const data = JSON.parse(text);

      if (!data.ok) {
        return {
          exactMatch: false,
          word: word.trim(),
          suggestions: [],
          distances: [],
        };
      }

      return data;
    } catch (error) {
      // Return a fallback response on network errors (CORS, network failure, etc.)
      return {
        exactMatch: false,
        word: word.trim(),
        suggestions: [],
        distances: [],
      };
    }
  }

  /**
   * Get a hint word from the day's hint list (daily_hints/{lang}/{date}.json). Requires at least one guess.
   * @param {string} lang - Language code (e.g. 'en', 'fr')
   * @param {string} dayKey - Optional day key for a specific day's puzzle
   * @param {string[]} guessedWords - Words already guessed (normalized lower case)
   * @param {number|null} bestScore - Score of the user's best guess so far (must be non-null; hints require at least one guess)
   * @returns {Promise<{ ok: boolean, word?: string, error?: string }>}
   */
  /**
   * Optional explainable-hint copy for the day's hint words (empty object if no file or none loaded).
   * @param {string} lang
   * @param {string|null} dayKey
   * @returns {Promise<{ ok: boolean, explanations?: Record<string, string> }>}
   */
  async function getHintExplanations(lang = 'en', dayKey = null) {
    let url = `${baseUrl}/api/hint-explanations?lang=${encodeURIComponent(lang)}`;
    if (dayKey) {
      url += `&dayKey=${encodeURIComponent(dayKey)}`;
    }
    try {
      const response = await fetch(url);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { ok: false, explanations: {} };
      }
      return data && typeof data === 'object'
        ? { ok: data.ok !== false, explanations: data.explanations && typeof data.explanations === 'object' ? data.explanations : {} }
        : { ok: false, explanations: {} };
    } catch {
      return { ok: false, explanations: {} };
    }
  }

  async function getHint(lang = 'en', dayKey = null, guessedWords = [], bestScore = null) {
    const url = `${baseUrl}/api/hint`;
    try {
      const body = { lang, guessedWords, bestScore: bestScore == null ? null : bestScore };
      if (dayKey) body.dayKey = dayKey;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        const e = new Error(data.error || 'Failed to get hint');
        if (data.code === 'HINTS_LIMIT_REACHED') e.code = 'HINTS_LIMIT_REACHED';
        if (typeof data.hintLimit === 'number') e.hintLimit = data.hintLimit;
        throw e;
      }
      return data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Report a word as valid (user says dictionary is wrong)
   * @param {string} word - The word the user reported as valid
   * @param {string} lang - Language code (e.g., 'en', 'el')
   * @param {string} dayKey - Optional day key
   * @returns {Promise<Object>} Response with ok
   */
  async function reportValidWord(word, lang = 'en', dayKey = null) {
    const url = `${baseUrl}/api/report-valid-word`;
    try {
      const body = { word: (word || '').trim(), lang };
      if (dayKey) body.dayKey = dayKey;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to report word');
      }
      return data;
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }

  /**
   * Save a winning guess to the database
   * @param {string} dayKey - The day key (e.g., "2026-01-25")
   * @param {string} language - Language code (e.g., 'en', 'el')
   * @param {string} winningWord - The word that was correctly guessed
   * @param {Array} guesses - Array of all valid guesses made by the user
   * @returns {Promise<Object>} Response with success status
   */
  async function saveWinningGuess(dayKey, language, winningWord, guesses) {
    const url = `${baseUrl}/api/save-winning-guess`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dayKey,
          language,
          winningWord,
          guesses,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Failed to save winning guess');
      }

      return data;
    } catch (err) {
      // Don't throw - this is optional functionality
      return { ok: false, error: err.message };
    }
  }

  return {
    submitGuess,
    getMeta,
    findClosestWords,
    getHint,
    getHintExplanations,
    saveWinningGuess,
    reportValidWord,
  };
}
